import React, { useState, useEffect } from 'react';
import './Header.css';
import Logo from './Erasys-logo.png';
import { FaBars, FaTimes, FaSignInAlt, FaSignOutAlt } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';

async function checkLoginStatus() {
  try {
    const response = await fetch('/api/profile', {
      method: 'GET',
      credentials: 'include',
    });
    return response.ok;
  } catch (error) {
    console.error('Error checking login status:', error);
    return false;
  }
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsMenuOpen, setIsProductsMenuOpen] = useState(false);
  const [isPricingMenuOpen, setIsPricingMenuOpen] = useState(false);
  const [isServicesMenuOpen, setIsServicesMenuOpen] = useState(false);
  const [isResourcesMenuOpen, setIsResourcesMenuOpen] = useState(false);
  const [isPortalsMenuOpen, setIsPortalsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    (async () => {
      const loggedIn = await checkLoginStatus();
      setIsLoggedIn(loggedIn);
    })();
  }, []);

  // This effect resets the menu state on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsProductsMenuOpen(false);
    setIsPricingMenuOpen(false);
    setIsServicesMenuOpen(false);
    setIsResourcesMenuOpen(false);
    setIsPortalsMenuOpen(false);
  }, [location.pathname]);

  // Toggle function for the hamburger icon
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsProductsMenuOpen(false);
    setIsPricingMenuOpen(false);
    setIsServicesMenuOpen(false);
    setIsResourcesMenuOpen(false);
    setIsPortalsMenuOpen(false);
  };

  // New function to always close the menu
  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsProductsMenuOpen(false);
    setIsPricingMenuOpen(false);
    setIsServicesMenuOpen(false);
    setIsResourcesMenuOpen(false);
    setIsPortalsMenuOpen(false);
  };

  const toggleProductsMenu = (e) => {
    e.stopPropagation();
    setIsProductsMenuOpen((prev) => !prev);
    setIsPricingMenuOpen(false);
    setIsServicesMenuOpen(false);
    setIsResourcesMenuOpen(false);
    setIsPortalsMenuOpen(false);
  };

  const togglePricingMenu = (e) => {
    e.stopPropagation();
    setIsPricingMenuOpen((prev) => !prev);
    setIsProductsMenuOpen(false);
    setIsServicesMenuOpen(false);
    setIsResourcesMenuOpen(false);
    setIsPortalsMenuOpen(false);
  };

  const toggleServicesMenu = (e) => {
    e.stopPropagation();
    setIsServicesMenuOpen((prev) => !prev);
    setIsProductsMenuOpen(false);
    setIsPricingMenuOpen(false);
    setIsResourcesMenuOpen(false);
    setIsPortalsMenuOpen(false);
  };

  const toggleResourcesMenu = (e) => {
    e.stopPropagation();
    setIsResourcesMenuOpen((prev) => !prev);
    setIsProductsMenuOpen(false);
    setIsPricingMenuOpen(false);
    setIsServicesMenuOpen(false);
    setIsPortalsMenuOpen(false);
  };

  const togglePortalsMenu = (e) => {
    e.stopPropagation();
    setIsPortalsMenuOpen((prev) => !prev);
    setIsProductsMenuOpen(false);
    setIsPricingMenuOpen(false);
    setIsServicesMenuOpen(false);
    setIsResourcesMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      const response = await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        setIsLoggedIn(false);
        closeMenu();
        navigate('/');
      } else {
        console.error('Logout failed.');
      }
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <header className="header">
      <div className="hamburger-icon" onClick={toggleMenu} role="button" tabIndex={0}>
        {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
      </div>

      <div className="logo1">
        <Link to="/" onClick={closeMenu}>
          <img src={Logo} alt="Erasys Logo" />
        </Link>
      </div>

      <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
        <ul>
          <li className={location.pathname === '/' ? 'active' : ''}>
            <Link to="/" onClick={closeMenu}>Home</Link>
          </li>

          {/* Products Menu */}
          <li 
            className={`dropdown ${isProductsMenuOpen ? 'open' : ''}`} 
            onMouseLeave={() => setIsProductsMenuOpen(false)}
          >
            <button onClick={toggleProductsMenu} className="dropdown-toggle">
              Products
            </button>
            {isProductsMenuOpen && (
              <ul className="submenu">
                <li>
                  <Link to="/products#whitematter" onClick={closeMenu}>
                    WhiteMatter
                  </Link>
                </li>
                <li>
                  <Link to="/safepulse" onClick={closeMenu}>
                    SafePulse
                  </Link>
                </li>
                <li>
                  <Link to="/safepulse-browser" onClick={closeMenu}>
                    SafePulse Browser
                  </Link>
                </li>
                <li>
                  <Link to="/trustmark" onClick={closeMenu}>
                    TrustMark
                  </Link>
                </li>
                <li>
                  <Link to="/clearframe" onClick={closeMenu}>
                    ClearFrame
                  </Link>
                </li>
                <li>
                  <Link to="/clearsense" onClick={closeMenu}>
                    ClearSense
                  </Link>
                </li>
                <li>
                  <Link to="/clearseal" onClick={closeMenu}>
                    ClearSeal
                  </Link>
                </li>
              </ul>
            )}
          </li>

          {/* Services Menu */}
          <li 
            className={`dropdown ${isServicesMenuOpen ? 'open' : ''}`} 
            onMouseLeave={() => setIsServicesMenuOpen(false)}
          >
            <button onClick={toggleServicesMenu} className="dropdown-toggle">
              Services
            </button>
            {isServicesMenuOpen && (
              <ul className="submenu">
                <li>
                  <Link to="/cybersecurity" onClick={closeMenu}>
                    Cybersecurity Consulting
                  </Link>
                </li>
                <li>
                  <Link to="/hrconsulting" onClick={closeMenu}>
                    HR Consulting
                  </Link>
                </li>
                <li>
                  <Link to="/wellness-risk-assessment" onClick={closeMenu}>
                    Wellness Risk Assessment
                  </Link>
                </li>
                <li>
                  <Link to="/cybersecurity-risk-assessment" onClick={closeMenu}>
                    Cybersecurity Risk Assessment
                  </Link>
                </li>
              </ul>
            )}
          </li>

          {/* Pricing Menu */}
          <li className={`dropdown ${isPricingMenuOpen ? 'open' : ''}`} 
              onMouseLeave={() => setIsPricingMenuOpen(false)}>
            <button onClick={togglePricingMenu} className="dropdown-toggle">
              Pricing
            </button>
            {isPricingMenuOpen && (
              <ul className="submenu">
                <li>
                  <Link to="/pricing" onClick={closeMenu}>
                    WhiteMatter
                  </Link>
                </li>
                <li>
                  <Link to="/TrustMark-pricing" onClick={closeMenu}>
                    TrustMark
                  </Link>
                </li>
              </ul>
            )}
          </li>

          <li className={location.pathname === '/contact' ? 'active' : ''}>
            <Link to="/contact" onClick={closeMenu}>Contact Us</Link>
          </li>

          <li 
            className={`dropdown ${isResourcesMenuOpen ? 'open' : ''}`} 
            onMouseLeave={() => setIsResourcesMenuOpen(false)}
          >
            <button onClick={toggleResourcesMenu} className="dropdown-toggle">
              Resources
            </button>
            {isResourcesMenuOpen && (
              <ul className="submenu">
                <li>
                  <Link to="/blogs" onClick={closeMenu}>
                    Blogs
                  </Link>
                </li>
                <li>
                  <Link to="/whitepapers" onClick={closeMenu}>
                    Whitepapers
                  </Link>
                </li>
              </ul>
            )}
          </li>

          <li className={location.pathname === '/download' ? 'active' : ''}>
            <Link to="/download" onClick={closeMenu}>Download</Link>
          </li>

          {/* Portals */}
          <li 
            className={`dropdown ${isPortalsMenuOpen ? 'open' : ''}`}
            onMouseLeave={() => setIsPortalsMenuOpen(false)}
          >
            <button onClick={togglePortalsMenu} className="dropdown-toggle">
              Portals
            </button>
            {isPortalsMenuOpen && (
              <ul className="submenu">
                <li>
                  <Link to="/partnerportal" onClick={closeMenu}>
                    Partner Portal
                  </Link>
                </li>
                <li>
                  <Link to="/training" onClick={closeMenu}>
                    Training Portal
                  </Link>
                </li>
              </ul>
            )}
          </li>

          {isLoggedIn && (
            <li className={location.pathname === '/account' ? 'active' : ''}>
              <Link to="/account" onClick={closeMenu}>Account</Link>
            </li>
          )}

          {isMenuOpen && (
            <>
              {isLoggedIn ? (
                <>
                  <li className="mobile-auth-link">
                    <Link to="/account" onClick={closeMenu}>Account</Link>
                  </li>
                  <li className="mobile-auth-link">
                    <button className="logout-button" onClick={handleLogout}>
                      <FaSignOutAlt className="icon" /> Logout
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li className="mobile-auth-link">
                    <Link to="/signin" onClick={closeMenu}>Sign In</Link>
                  </li>
                  <li className="mobile-auth-link">
                    <Link to="/signup" onClick={closeMenu}>Sign Up</Link>
                  </li>
                </>
              )}
            </>
          )}
        </ul>
      </nav>

      <div className="actions">
        
        <div className="desktop-auth-buttons">
          {isLoggedIn ? (
            <button className="logout-button" onClick={handleLogout}>
              <FaSignOutAlt className="icon" /> Logout
            </button>
          ) : (
            <button className="login-button" onClick={() => { closeMenu(); navigate('/signin'); }}>
              <FaSignInAlt className="icon" /> Login
            </button>
          )}
        </div>
      </div>

      {isMenuOpen && <div className="menu-overlay" onClick={closeMenu} />}
    </header>
  );
};

export default Header;
